import { ONBOARDING_DONE } from '@/constants'
import { action, createRequestTypes } from '@/utils/actions.utils'
import { authService, localStorageService } from '@/services'
import { uiActions } from '@/redux/actions'
import { cleanCookie } from '@/utils/cookies.utils'
import Toggles from '@domain/toggles/Toggles'
import { __clearMigratedRoutesCache } from '@/routers/migratedRoutesClient'

export const LOGGED = 'LOGGED'
const logged = () => {
	// The strangler's migrated-routes cache is keyed by caller identity server-side, but
	// cached client-side for 30s regardless — without this, a route like '/' (anonymous-only)
	// can serve a stale answer computed before login for up to that long.
	__clearMigratedRoutesCache()
	return action(LOGGED)
}

// Distinct from LOGGED: marks an existing, still-valid session as logged in without
// the account-loaded guarantee LOGGED carries — GTMAuthActionsEvents.middleware.js
// reads `state.auth.account` unconditionally on LOGGED and assumes it's already
// populated. Used when we skip the refresh round-trip entirely (see AuthContext.js).
export const SESSION_RESTORED = 'SESSION_RESTORED'
const sessionRestored = () => {
	return action(SESSION_RESTORED)
}

export const MODAL_AUTH_CLOSED = 'MODAL_AUTH_CLOSED'
const modalAuthClosed = () => {
	return action(MODAL_AUTH_CLOSED)
}

export const LOGIN_TIMEOUT = 'LOGIN_TIMEOUT'
const loginTimeout = (payload) => {
	return action(LOGIN_TIMEOUT, payload)
}

export const SIGN_UP_SUCCESS = 'SIGN_UP_SUCCESS'
const signUpSuccess = (payload) => {
	return action(SIGN_UP_SUCCESS, payload)
}

export const SIGN_UP_SOCIAL_ERROR = 'SIGN_UP_SOCIAL_ERROR'
const signUpSocialError = (payload) => {
	return action(SIGN_UP_SOCIAL_ERROR, payload)
}

export const SIGN_UP_SOCIAL_SUCCESS = 'SIGN_UP_SOCIAL_SUCCESS'
const signUpSocialSuccess = (payload) => {
	return action(SIGN_UP_SOCIAL_SUCCESS, payload)
}

export const QUALIFIED_SUCCESS = 'QUALIFIED_SUCCESS'
const qualifiedSuccess = () => {
	return action(QUALIFIED_SUCCESS)
}

export const QUALIFIED_ERROR = 'QUALIFIED_ERROR'
const qualifiedError = (payload) => {
	return action(QUALIFIED_ERROR, payload)
}

export const SUPPORT_CREDENTIALS_ERROR = 'SUPPORT_CREDENTIALS_ERROR'
const supportCredentialsError = (payload) => {
	return action(SUPPORT_CREDENTIALS_ERROR, payload)
}

export const GET_ACCOUNT_DETAILS = createRequestTypes('GET_ACCOUNT_DETAILS')

// The v3 /account path goes through ApiCaller (raw fetch), which throws
// `{ ErrorCode }`, while the v1 path goes through axios and throws
// `error.response.status`. Normalise both so a 401 is detected either way.
const isUnauthorizedError = (error) =>
	error?.ErrorCode === 401 ||
	error?.status === 401 ||
	error?.response?.status === 401

const getAccountDetails = (logErrors = true, silent = false) => {
	return async (dispatch) => {
		try {
			await dispatch(request({ silent }))

			const res = await authService.getAccountDetails()
			return dispatch(success(res.data))
		} catch (error) {
			// checkSessionOnGetAccount: a 401 on /account means the session is no
			// longer valid (logged out, revoked, or a token from another
			// environment). The v3 account path bypasses the authClient refresh
			// interceptor, so nothing clears the dead session — it just surfaces the
			// generic error modal. Clear the session cookies and send the user home.
			if (Toggles.isEnabled('checkSessionOnGetAccount') && isUnauthorizedError(error)) {
				cleanCookie('vibia_jwt')
				cleanCookie('vibia_refresh')
				if (typeof window !== 'undefined') {
					window.location.assign('/')
				}
				return dispatch(failure(error))
			}
			if (logErrors) {
				return dispatch(failure(error))
			}
			return dispatch(success(false))
		}
	}

	function request(payload) {
		return action(GET_ACCOUNT_DETAILS.REQUEST, payload)
	}
	function success(payload) {
		return action(GET_ACCOUNT_DETAILS.SUCCESS, payload)
	}
	function failure(err) {
		return action(GET_ACCOUNT_DETAILS.FAILURE, null, err)
	}
}

export const LOGOUT = createRequestTypes('LOGOUT')
const logout = (isCurrentTokenValid = true, withNavigation = true) => {
	return async (dispatch) => {
		try {
			await dispatch(request())

			if (isCurrentTokenValid) {
				await authService.doLogout()
			}

			if (Toggles.isEnabled('logoutv3')) {
				cleanCookie('vibia_jwt')
				cleanCookie('vibia_refresh')
			}

			// See the comment on logged() above — same staleness risk in the other direction:
			// without this, '/' can keep resolving as "authenticated" (servedByNext: false,
			// stay on eproject's own home) for up to 30s after logging out.
			__clearMigratedRoutesCache()

			// Reload path: navigate before dispatching success(). Flipping `logged`/`account` first
			// re-renders eproject's still-mounted router into an intermediate anonymous view while
			// the reload is in flight — that transient render is the post-logout flicker.
			if (withNavigation && typeof window !== 'undefined' && Toggles.isEnabled('newProfesionalArea')) {
				window.location.assign('/')
				return
			}

			if (withNavigation) {
				dispatch(
					uiActions.setNavigation({
						preferredByUser: false,
						hasBeenActive: false
					})
				)
			}

			localStorageService.removeKey(ONBOARDING_DONE)

			return dispatch(success())
		} catch (error) {
			return dispatch(failure(error))
		}
	}

	function request() {
		return action(LOGOUT.REQUEST)
	}
	function success() {
		return action(LOGOUT.SUCCESS)
	}
	function failure(err) {
		return action(LOGOUT.FAILURE, null, err, false)
	}
}

export const SAVE_AUTH_TOKEN = 'SAVE_AUTH_TOKEN'
const saveAuthToken = (token) => {
	return {
		type: SAVE_AUTH_TOKEN,
		payload: token
	}
}

export const REFRESH_TOKEN = createRequestTypes('REFRESH_TOKEN')
const refreshToken = () => {
	return async (dispatch) => {
		await dispatch(request())
		try {
			const res = await authService.refreshToken()
			console.log('Token refreshed.')
			return dispatch(success(res.data))
		} catch(error) {
			return dispatch(failure(error))
		}
	}

	function request() {
		return action(REFRESH_TOKEN.REQUEST)
	}
	function success(payload) {
		return action(REFRESH_TOKEN.SUCCESS, payload)
	}
	function failure(err) {
		return action(REFRESH_TOKEN.FAILURE, null, err, false)
	}
}

export const SHOW_MODAL_AUTH = 'SHOW_MODAL_AUTH'
const showModalAuth = (value) => {
	return action(SHOW_MODAL_AUTH, value)
}

export default {
	logged,
	sessionRestored,
	modalAuthClosed,
	loginTimeout,
	getAccountDetails,
	saveAuthToken,
	signUpSuccess,
	signUpSocialSuccess,
	signUpSocialError,
	qualifiedSuccess,
	qualifiedError,
	logout,
	refreshToken,
	supportCredentialsError,
	showModalAuth,
}
